import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    CredentialStoreGenericType,
    CredentialStoreResourceType,
    ICredentialStore,
    ICredentialStoreConfigurationEntry,
    ICredentialStoreResource,
    ICredentialStoreType,
    ODataResponse,
} from '@models';
import { ODataQueryOptions } from '@services';
import { ODataService } from '@services/generics/odata.service';
import { MapperService } from '@services/mapper.service';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class CredentialStoresService extends ODataService<ICredentialStore, number> {
    protected _endpoint = '/odata/CredentialStores';
    constructor(http: HttpClient) {
        super(http, MapperService.credentialStore);
    }

    public getCredentialStoreTypes(): Observable<ICredentialStoreType[]> {
        const endpoint = `${this._endpoint}/UiPath.Server.Configuration.OData.GetAvailableCredentialStoreTypes()`;

        return this._http.get<{ value: string[] }>(endpoint)
            .pipe(
                map(data => data.value.map(
                    storeType => ({
                        value: storeType,
                        genericType: storeType === 'Database' ?
                            CredentialStoreGenericType.Database :
                            CredentialStoreGenericType.SecureStore,
                    }),
                )),
            );
    }

    public setDefaultStore(credentialStore: ICredentialStore, resourceType: CredentialStoreResourceType) {
        const endpoint = `${this._endpoint}(${credentialStore.id})/UiPath.Server.Configuration.OData.SetDefaultStoreForResourceType`;
        const payload = { resourceType: CredentialStoreResourceType[resourceType] };

        return this._http.post(endpoint, JSON.stringify(payload));
    }

    public getDefaultStore(resourceType: CredentialStoreResourceType): Observable<number> {
        // tslint:disable-next-line: max-line-length
        const endpoint = `${this._endpoint}/UiPath.Server.Configuration.OData.GetDefaultStoreForResourceType(resourceType='${CredentialStoreResourceType[resourceType]}')`;

        return this._http.get(endpoint)
            .pipe(
                map((response: any) => response.value),
            ) as Observable<number>;
    }

    public getResourcesForCredentialStore(
        credentialStore: ICredentialStore,
        resourceType: CredentialStoreResourceType,
        queryOptions?: ODataQueryOptions,
    ) {
        // tslint:disable-next-line: max-line-length
        const endpoint = `${this._endpoint}/UiPath.Server.Configuration.OData.GetResourcesForCredentialStoreTypes(key=${credentialStore.id},resourceType='${CredentialStoreResourceType[resourceType]}')`;

        return this._http.get(`${endpoint}${this.getQueryString(queryOptions)}`)
            .pipe(
                map(response => this._mapData(response, MapperService.credentialTypeResource)),
            ) as Observable<ODataResponse<ICredentialStoreResource>>;
    }

    public deleteCredentialStore(credentialStore: ICredentialStore) {
        const endpoint = `${this._endpoint}(${credentialStore.id})/UiPath.Server.Configuration.OData.Delete`;
        return this._http.post(endpoint, JSON.stringify({
            key: credentialStore.id,
            forceDelete: true,
        }));
    }

    public getSecureCredentialStoreConfiguration(name: string) {
        const endpoint = `/odata/Settings/UiPath.Server.Configuration.OData.GetSecureStoreConfiguration(storeTypeName='${name}')`;
        return this._http.get(endpoint)
            .pipe(
                map(response => this._mapData(response, MapperService.credentialStoreConfigurationEntryMap) as
                    ODataResponse<ICredentialStoreConfigurationEntry>),
                map(response => response.data),
            );
    }
}
